import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, DatePicker, Flex, Input, Popconfirm, Select, Tag, Space, Tooltip, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined, SwapOutlined, CopyOutlined, UploadOutlined, MailOutlined, MessageOutlined, TagsOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import { IoLogoWhatsapp } from 'react-icons/io';
import SendWAMessageDrawer from './SendWAMessageDrawer';
import SendEmailDrawer from './SendEmailDrawer';
import ImportLeadsModal from './ImportLeadsModal';
import LeadTagsCell from './LeadTagsCell';
import ManageLeadTagsModal from './ManageLeadTagsModal';
import BulkEditLeadTagsModal from './BulkEditLeadTagsModal';
import { LeadTagSelect, nextLeadTagColor } from './leadTags';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import PageActions from 'components/Common/PageActions';
import ProductLabelsCell from 'components/Common/ProductLabelsCell';
import PhoneNumberCell from 'components/Common/PhoneNumberCell';
import LastActivityCell, { sortByLastActivity } from 'components/Common/LastActivityCell';
import { copyText } from 'utils/clipboard';
import { LEAD_STATUS_OPTIONS, leadStatusMeta } from './leadStatus';
import useUrlFilters from 'hooks/useUrlFilters';

// Mirrors LeadActivity.ActivityTypeChoices on the backend.
const LAST_ACTIVITY_TYPE_OPTIONS = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'CALL', label: 'Call' },
  { value: 'OTHER', label: 'Other' },
];

// Every filter, the search and the page live in the URL so a filtered list can
// be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
  products: { type: 'array', key: 'product' },
  tags: { type: 'array', key: 'tag' },
  source: { type: 'string' },
  status: { type: 'array' },
  assignedTo: { type: 'string', key: 'assigned_to' },
  created: { type: 'dateRange', keys: ['created_after', 'created_before'] },
  activityTypeOp: { type: 'string', key: 'activity_type_op', default: 'is' },
  activityTypes: { type: 'array', key: 'activity_type' },
  activityTimeOp: { type: 'string', key: 'activity_time_op', default: 'after' },
  activityAt: { type: 'datetime', key: 'activity_at' },
  activityRange: { type: 'dateRange', keys: ['activity_from', 'activity_to'], withTime: true },
  // true / false / unset (either way).
  lastActivityFailed: { type: 'boolean', key: 'last_activity_failed' },
  hasEmail: { type: 'boolean', key: 'has_email' },
  hasPhone: { type: 'boolean', key: 'has_phone' },
  hasWhatsapp: { type: 'boolean', key: 'has_whatsapp' },
};

// Contact-detail filters: each is "has it" / "missing it" / any, combined with AND.
const CONTACT_FILTERS = [
  { name: 'hasEmail', param: 'has_email', label: 'Email' },
  { name: 'hasPhone', param: 'has_phone', label: 'Phone' },
  { name: 'hasWhatsapp', param: 'has_whatsapp', label: 'WhatsApp' },
];

// antd Select values are strings; the filters are booleans.
const toChoice = (value) => (value === true ? 'yes' : value === false ? 'no' : undefined);
const fromChoice = (choice) => (choice === 'yes' ? true : choice === 'no' ? false : null);


const LeadsTable = () => {

  const navigate = useNavigate();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  // The API caps a page at 1000 (StandardPagination.max_page_size); clamp a
  // hand-edited ?page_size= so paging maths matches what actually comes back.
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [selectionType] = useState('checkbox');
  const [openWAMessageDrawer, setOpenWAMessageDrawer] = useState(false);
  const [openEmailDrawer, setOpenEmailDrawer] = useState(false);
  const [openImportDrawer, setOpenImportDrawer] = useState(false);
  const [openTagsModal, setOpenTagsModal] = useState(false);
  const [openBulkTagsModal, setOpenBulkTagsModal] = useState(false);

  const [selectedRecords, setSelectedRecords] = useState([]);

  const [loading, setLoading] = useState(false);

  // Products come from subscription billing, and are both the labels put on a
  // lead and the values the Products filter offers.
  const [products, setProducts] = useState([]);
  const filterProducts = filters.products;
  const setFilterProducts = urlSetter('products');
  // Lead tags in the user's order; null until loaded. Shared by the Tags
  // column, the Tags filter and the Tags modal.
  const [leadTags, setLeadTags] = useState(null);
  const filterTags = filters.tags;
  const setFilterTags = urlSetter('tags');
  const filterSource = filters.source || '';
  const setFilterSource = urlSetter('source');
  const filterStatus = filters.status;
  const setFilterStatus = urlSetter('status');
  const filterAssignedTo = filters.assignedTo || undefined;
  const setFilterAssignedTo = urlSetter('assignedTo');
  const filterCreated = filters.created;
  const setFilterCreated = urlSetter('created');
  // Last activity: channel ("is" / "is not" any of the picked types) and when
  // it happened (after / before a datetime, or between two).
  const filterActivityTypeOp = filters.activityTypeOp;
  const setFilterActivityTypeOp = urlSetter('activityTypeOp');
  const filterActivityTypes = filters.activityTypes;
  const setFilterActivityTypes = urlSetter('activityTypes');
  const filterActivityTimeOp = filters.activityTimeOp;
  // A single datetime and a range sit in different params.
  const filterActivityTime = filterActivityTimeOp === 'between' ? filters.activityRange : filters.activityAt;
  const setFilterActivityTime = (value) => setFilters(
    filterActivityTimeOp === 'between' ? { activityRange: value } : { activityAt: value }
  );
  const [users, setUsers] = useState([]);

  const lastActivityParams = () => {
    const params = {};
    if (filterActivityTypes.length) {
      params[filterActivityTypeOp === 'is' ? 'last_activity_type' : 'last_activity_type_not'] = filterActivityTypes;
    }
    if (filterActivityTime) {
      const [from, to] = filterActivityTimeOp === 'between'
        ? filterActivityTime
        : filterActivityTimeOp === 'after' ? [filterActivityTime, null] : [null, filterActivityTime];
      if (from) params.last_activity_after = from.toISOString();
      if (to) params.last_activity_before = to.toISOString();
    }
    return params;
  };

  const fetchLeads = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadLeads(page, searchTerm, size, {
        products: filterProducts,
        tags: filterTags,
        source: filterSource,
        status: filterStatus,
        assigned_to: filterAssignedTo,
        created_after: filterCreated?.[0]?.format('YYYY-MM-DD'),
        created_before: filterCreated?.[1]?.format('YYYY-MM-DD'),
        ...lastActivityParams(),
        last_activity_failed: filters.lastActivityFailed ?? undefined,
        ...Object.fromEntries(
          CONTACT_FILTERS.map(({ name, param }) => [param, filters[name] ?? undefined])
        ),
      });
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error('Error fetching leads:', e);
    }
    setLoading(false);
  }

  const fetchProducts = async () => {
    const response = await actions.loadProductPaymentProducts();
    if (response && response.data) {
      setProducts(Array.isArray(response.data) ? response.data : response.data.results || []);
    }
  }

  const fetchLeadTags = async () => {
    try {
      setLeadTags(await actions.listLeadTags());
    } catch (e) {
      console.error('Error fetching lead tags:', e);
      setLeadTags([]);
    }
  }

  // Owners for the "Assigned To" filter.
  const fetchUsers = async () => {
    const response = await actions.loadAllUsers();
    if (response && response.data) {
      setUsers(response.data.results || response.data || []);
    }
  }

  const userLabel = (user) =>
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.id;

  useEffect(() => {
    fetchProducts();
    fetchUsers();
    fetchLeadTags();
  }, []);

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  }

  // Fetch leads when search term, product filter or pagination changes
  useEffect(() => {
    fetchLeads(currentPage, pageSize);
  }, [
    searchTerm, currentPage, pageSize, filterProducts, filterTags, filterSource, filterStatus, filterAssignedTo, filterCreated,
    filterActivityTypeOp, filterActivityTypes, filterActivityTimeOp, filterActivityTime,
    filters.lastActivityFailed, filters.hasEmail, filters.hasPhone, filters.hasWhatsapp,
  ]);

  const onFilterProductsChange = (value) => {
    setFilterProducts(value || []);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterProducts([]);
    setFilterTags([]);
    setFilterSource('');
    setFilterStatus([]);
    setFilterAssignedTo(undefined);
    setFilterCreated(null);
    setFilterActivityTypes([]);
    setFilters({
      activityAt: null, activityRange: null,
      lastActivityFailed: null, hasEmail: null, hasPhone: null, hasWhatsapp: null,
    });
    setCurrentPage(1);
  };

  // Booleans set straight on the URL, back to page 1 like every filter.
  const setBooleanFilter = (name) => (choice) => setFilters({ [name]: fromChoice(choice), page: 1 });

  // Any filter change puts you back on page 1 - staying on page 4 of a list
  // that just shrank to two pages shows an empty table.
  const applyFilter = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const activeFilterCount =
    filterProducts.length + filterTags.length + filterStatus.length + (filterSource ? 1 : 0)
    + (filterAssignedTo ? 1 : 0) + (filterCreated ? 1 : 0)
    + (filterActivityTypes.length ? 1 : 0) + (filterActivityTime ? 1 : 0)
    + (filters.lastActivityFailed != null ? 1 : 0)
    + CONTACT_FILTERS.filter(({ name }) => filters[name] != null).length;

  const activityTypeLabel = (type) =>
    LAST_ACTIVITY_TYPE_OPTIONS.find((option) => option.value === type)?.label || type;

  const formatActivityTime = (value) => value.format('D MMM YYYY, h:mm A');

  const activityTimeChipLabel = () => {
    if (!filterActivityTime) return '';
    if (filterActivityTimeOp === 'between') {
      return `${formatActivityTime(filterActivityTime[0])} - ${formatActivityTime(filterActivityTime[1])}`;
    }
    return `${filterActivityTimeOp === 'after' ? 'after' : 'before'} ${formatActivityTime(filterActivityTime)}`;
  };

  // One removable chip per applied value.
  const activeFilters = [
    ...filterProducts.map((productId) => ({
      key: `product-${productId}`,
      label: `Product: ${products.find((p) => p.id === productId)?.name || productId}`,
      onRemove: () =>
        onFilterProductsChange(filterProducts.filter((value) => value !== productId)),
    })),
    ...filterTags.map((tagId) => ({
      key: `tag-${tagId}`,
      label: `Tag: ${(leadTags || []).find((t) => t.id === tagId)?.name || tagId}`,
      onRemove: () => applyFilter(setFilterTags)(filterTags.filter((value) => value !== tagId)),
    })),
    ...filterStatus.map((status) => ({
      key: `status-${status}`,
      label: `Status: ${leadStatusMeta(status).label}`,
      onRemove: () => applyFilter(setFilterStatus)(filterStatus.filter((value) => value !== status)),
    })),
    filterSource && {
      key: 'source',
      label: `Source: ${filterSource}`,
      onRemove: () => applyFilter(setFilterSource)(''),
    },
    filterAssignedTo && {
      key: 'assigned',
      label: `Assigned: ${userLabel(users.find((u) => u.id === filterAssignedTo) || {})}`,
      onRemove: () => applyFilter(setFilterAssignedTo)(undefined),
    },
    filterCreated && {
      key: 'created',
      label: `Created: ${filterCreated[0].format('D MMM YYYY')} - ${filterCreated[1].format('D MMM YYYY')}`,
      onRemove: () => applyFilter(setFilterCreated)(null),
    },
    filterActivityTypes.length > 0 && {
      key: 'activity-type',
      label: `Last activity ${filterActivityTypeOp === 'is' ? 'is' : 'is not'}: ${filterActivityTypes.map(activityTypeLabel).join(' / ')}`,
      onRemove: () => applyFilter(setFilterActivityTypes)([]),
    },
    filterActivityTime && {
      key: 'activity-time',
      label: `Last activity ${activityTimeChipLabel()}`,
      onRemove: () => applyFilter(setFilterActivityTime)(null),
    },
    filters.lastActivityFailed != null && {
      key: 'activity-failed',
      label: filters.lastActivityFailed ? 'Last activity failed' : 'Last activity not failed',
      onRemove: () => setBooleanFilter('lastActivityFailed')(undefined),
    },
    ...CONTACT_FILTERS.filter(({ name }) => filters[name] != null).map(({ name, label }) => ({
      key: name,
      label: filters[name] ? `Has ${label}` : `No ${label}`,
      onRemove: () => setBooleanFilter(name)(undefined),
    })),
  ].filter(Boolean);

  const saveProducts = useCallback(async (leadId, productIds) => {
    const response = await actions.updateLeadProducts(leadId, productIds);
    // Keep the row in step with what was saved, so the labels survive a
    // re-render without refetching the whole page.
    setDataSource((rows) => rows.map((row) => (
      row.id === leadId ? { ...row, ...response.data } : row
    )));
    return response;
  }, []);

  // Tags ticked in the Tags column. LeadTagsCell shows the pick while this
  // is in flight, then the row patched here takes over.
  const saveTags = useCallback(async (leadId, tagIds) => {
    const response = await actions.updateLeadTags(leadId, tagIds);
    setDataSource((rows) => rows.map((row) => (
      row.id === leadId ? { ...row, tags: response.data.tags, tags_detail: response.data.tags_detail } : row
    )));
    return response;
  }, []);

  // Tags added to or removed from many leads at once. Patch the rows on
  // screen and the selection too, which may hold leads from other pages -
  // the bulk dialog reads the selection to know which tags can be removed.
  const applyBulkTags = useCallback((updatedLeads) => {
    const byId = new Map(updatedLeads.map((lead) => [lead.id, lead]));
    const patch = (rows) => rows.map((row) => (
      byId.has(row.id)
        ? { ...row, tags: byId.get(row.id).tags, tags_detail: byId.get(row.id).tags_detail }
        : row
    ));
    setDataSource(patch);
    setSelectedRecords(patch);
  }, []);

  // A tag created from a row's tag picker joins the shared list at the end.
  const createTag = useCallback(async (name) => {
    const tag = await actions.createLeadTag({ name, color: nextLeadTagColor(leadTags || []) });
    setLeadTags((list) => [...(list || []), tag]);
    return tag;
  }, [leadTags]);

  // "Save as WhatsApp number" from the phone column. The row is patched in
  // place so the WA Number cell next to it updates without a refetch.
  const saveWaNumber = useCallback(async (leadId, waNumber) => {
    const response = await actions.updateLeadWaNumber(leadId, waNumber);
    setDataSource((rows) => rows.map((row) => (
      row.id === leadId ? { ...row, ...response.data } : row
    )));
    return response;
  }, []);

  // Status picked straight from the table. Patched in place like the other
  // inline edits, and put back if the save fails.
  const saveStatus = useCallback(async (leadId, status, previous) => {
    setDataSource((rows) => rows.map((row) => (
      row.id === leadId ? { ...row, status } : row
    )));
    try {
      await actions.updateLeadStatus(leadId, status);
      message.success(`Status set to ${leadStatusMeta(status).label}`);
    } catch (e) {
      setDataSource((rows) => rows.map((row) => (
        row.id === leadId ? { ...row, status: previous } : row
      )));
      message.error('Could not update the status');
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    const response = await actions.deleteLeads(id);
    console.log(response);
    if (response) {
      // Refresh the data after deletion
      fetchLeads(currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  const copyLeadNumber = useCallback(async (leadNumber) => {
    const copied = await copyText(leadNumber);
    message[copied ? 'success' : 'error'](
      copied ? 'Lead number copied!' : 'Could not copy the lead number'
    );
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      setSelectedRecords(selectedRows);

    },
  }


  const defaultColumns = useMemo(() => [
    {
      title: 'Number',
      dataIndex: 'lead_number',
      key: 'lead_number',
      sorter: (a, b) => (a?.lead_number || '').localeCompare(b?.lead_number || ''),
      ellipsis: true,
      width: 130,
      fixed: "left",
      render: (lead_number, record) => (
        <a
          href={`/leads/edit/${record.id}/`}
          onClick={(e) => e.stopPropagation()}
          style={{
            color: '#1890ff',
            textDecoration: 'none',
            cursor: 'pointer',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {lead_number}
        </a>
      ),
    },
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_activity',
      key: 'last_activity',
      sorter: sortByLastActivity,
      width: 160,
      render: (value) => <LastActivityCell value={value} />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a?.status || '').localeCompare(b?.status || ''),
      width: 170,
      render: (status, record) => (
        <Select
          size="small"
          variant="borderless"
          value={status}
          popupMatchSelectWidth={false}
          style={{ width: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onChange={(value) => saveStatus(record.id, value, status)}
          labelRender={() => {
            const { label, color } = leadStatusMeta(status);
            return <Tag color={color} style={{ marginInlineEnd: 0 }}>{label}</Tag>;
          }}
          options={LEAD_STATUS_OPTIONS.map(({ value, label, color }) => ({
            value,
            label: <Tag color={color} style={{ marginInlineEnd: 0 }}>{label}</Tag>,
          }))}
        />
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 240,
      render: (tagIds, record) => (
        <LeadTagsCell
          tagIds={tagIds}
          details={record.tags_detail}
          allTags={leadTags}
          onSave={(ids) => saveTags(record.id, ids)}
          onCreateTag={createTag}
        />
      ),
    },
    {
      title: 'Follow-up',
      dataIndex: 'follow_up_date',
      key: 'follow_up_date',
      sorter: (a, b) => (a?.follow_up_date || '').localeCompare(b?.follow_up_date || ''),
      width: 170,
      // A converted lead has nothing left to follow up.
      render: (value, record) => (
        record.status !== 'CONVERTED' && value ? dayjs(value).format('D MMM YYYY, h:mm A') : '-'
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a?.email || '').localeCompare(b?.email || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone_number',
      key: 'phone_number',
      sorter: (a, b) => (a?.phone_number || '').localeCompare(b?.phone_number || ''),
      width: 240,
      render: (phone_number, record) => (
        <PhoneNumberCell
          value={phone_number}
          waNumber={record.wa_number}
          onSaveWhatsapp={(number) => saveWaNumber(record.id, number)}
          recordLabel={[record.first_name, record.last_name].filter(Boolean).join(' ')}
        />
      ),
    },
    {
      title: 'WA Number',
      dataIndex: 'wa_number',
      key: 'wa_number',
      sorter: (a, b) => (a?.wa_number || '').localeCompare(b?.wa_number || ''),
      width: 240,
      // Already the WhatsApp number, so only copying and opening the chat
      // make sense here.
      render: (wa_number) => <PhoneNumberCell value={wa_number} waNumber={wa_number} />,
    },
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
      sorter: (a, b) => (a?.company_name || '').localeCompare(b?.company_name || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Job Title',
      dataIndex: 'job_title',
      key: 'job_title',
      sorter: (a, b) => (a?.job_title || '').localeCompare(b?.job_title || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      sorter: (a, b) => (a?.source || '').localeCompare(b?.source || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Products',
      dataIndex: 'products_detail',
      key: 'products_detail',
      width: 260,
      render: (value, record) => (
        <ProductLabelsCell
          value={value}
          options={products}
          onSave={(productIds) => saveProducts(record.id, productIds)}
        />
      ),
    },
    {
      title: 'Communication Method',
      dataIndex: 'communication_method',
      key: 'communication_method',
      sorter: (a, b) => (a?.communication_method || '').localeCompare(b?.communication_method || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Last Replied',
      dataIndex: 'last_replied_at',
      key: 'last_replied_at',
      // When the lead last wrote in, on any channel; never-replied sorts last.
      sorter: (a, b) =>
        (a?.last_replied_at ? dayjs(a.last_replied_at).valueOf() : 0) -
        (b?.last_replied_at ? dayjs(b.last_replied_at).valueOf() : 0),
      render: (value) => (value ? dayjs(value).format('D MMM YYYY, h:mm a') : '—'),
      width: 180,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      sorter: (a, b) => (a?.notes || '').localeCompare(b?.notes || ''),
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Actions',
      dataIndex: 'Actions',
      key: 'Actions',
      width: 180,
      render: (_, record) => (
        dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <Tooltip title="Copy Number">
              <a onClick={() => copyLeadNumber(record.lead_number)}>
                <CopyOutlined style={{ verticalAlign: 'middle' }} />
              </a>
            </Tooltip>
            <Tooltip title="Edit">
              <a href={`/leads/edit/${record.id}/`}>
                <EditOutlined style={{ verticalAlign: 'middle' }} />
              </a>
            </Tooltip>
            {!record.converted_at && (
              <Tooltip title="Convert to Deal">
                <a href={`/leads/convert/${record.id}/`}>
                  <SwapOutlined style={{ verticalAlign: 'middle', color: '#52c41a' }} />
                </a>
              </Tooltip>
            )}
            <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
              <a><DeleteOutlined /></a>
            </Popconfirm>
            {record?.wa_number && (
              <a href={`/whatsapp/chat?selectedContact=${record.wa_number}`}>
                <IoLogoWhatsapp color="#00B96B" size="15" />
              </a>
            )}
          </Flex>
        ) : null
      ),
    }
  ], [dataSource?.length, navigate, handleDelete, copyLeadNumber, products, saveProducts, saveWaNumber, saveStatus, leadTags, saveTags, createTag]);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };


  return (
    <div style={{ padding: 10 }}>
      <Flex gap="small" align="center" style={{ marginBottom: 8, minHeight: 24 }}>
        <span>
          Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} leads
        </span>
        {selectedRecords.length > 0 && (
          <>
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              {selectedRecords.length} selected
            </Tag>
            <Button type="link" size="small" onClick={() => setSelectedRecords([])}>
              Clear
            </Button>
          </>
        )}
      </Flex>
      <Flex
        gap="middle"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search name, email, phone, company, notes..."
            activeCount={activeFilterCount}
            activeFilters={activeFilters}
            onClear={clearFilters}
            title="Filter Leads"
            onRefresh={() => fetchLeads(currentPage, pageSize)}
            refreshing={loading}
          >
            <FilterField label="Products">
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Filter by product"
                value={filterProducts}
                onChange={onFilterProductsChange}
                options={products.map((item) => ({ value: item.id, label: item.name }))}
                optionFilterProp="label"
              />
            </FilterField>
            <FilterField label="Tags">
              <LeadTagSelect
                tags={leadTags || []}
                placeholder="Filter by tag"
                value={filterTags}
                onChange={(value) => applyFilter(setFilterTags)(value || [])}
              />
            </FilterField>
            <FilterField label="Status">
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Filter by status"
                value={filterStatus}
                onChange={(value) => applyFilter(setFilterStatus)(value || [])}
                options={LEAD_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
                optionFilterProp="label"
              />
            </FilterField>
            <FilterField label="Source">
              <Input
                allowClear
                placeholder="e.g. Referral, Website"
                value={filterSource}
                onChange={(event) => applyFilter(setFilterSource)(event.target.value)}
              />
            </FilterField>
            <FilterField label="Assigned To">
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="Filter by owner"
                value={filterAssignedTo}
                onChange={applyFilter(setFilterAssignedTo)}
                optionFilterProp="label"
                options={users.map((user) => ({ value: user.id, label: userLabel(user) }))}
              />
            </FilterField>
            <FilterField label="Created Between">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={filterCreated}
                onChange={applyFilter(setFilterCreated)}
              />
            </FilterField>
            <FilterField label="Last Activity Type">
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  style={{ width: 110, flexShrink: 0 }}
                  value={filterActivityTypeOp}
                  onChange={applyFilter(setFilterActivityTypeOp)}
                  options={[
                    { value: 'is', label: 'Is' },
                    { value: 'is_not', label: 'Is not' },
                  ]}
                />
                <Select
                  mode="multiple"
                  allowClear
                  style={{ flex: 1, minWidth: 0 }}
                  placeholder="WhatsApp, Email..."
                  value={filterActivityTypes}
                  onChange={(value) => applyFilter(setFilterActivityTypes)(value || [])}
                  options={LAST_ACTIVITY_TYPE_OPTIONS}
                  optionFilterProp="label"
                />
              </Space.Compact>
            </FilterField>
            <FilterField label="Last Activity Time">
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  style={{ width: 110, flexShrink: 0 }}
                  value={filterActivityTimeOp}
                  onChange={(value) => {
                    // A single datetime and a range don't convert into each other.
                    setFilters({ activityTimeOp: value, activityAt: null, activityRange: null, page: 1 });
                  }}
                  options={[
                    { value: 'after', label: 'After' },
                    { value: 'before', label: 'Before' },
                    { value: 'between', label: 'Between' },
                  ]}
                />
                {filterActivityTimeOp === 'between' ? (
                  <DatePicker.RangePicker
                    showTime={{ format: 'h:mm A' }}
                    format="D MMM YYYY, h:mm A"
                    style={{ flex: 1, minWidth: 0 }}
                    value={filterActivityTime}
                    onChange={applyFilter(setFilterActivityTime)}
                  />
                ) : (
                  <DatePicker
                    showTime={{ format: 'h:mm A' }}
                    format="D MMM YYYY, h:mm A"
                    placeholder="Select date & time"
                    style={{ flex: 1, minWidth: 0 }}
                    value={filterActivityTime}
                    onChange={applyFilter(setFilterActivityTime)}
                  />
                )}
              </Space.Compact>
            </FilterField>
            <FilterField label="Last Activity Delivery">
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any"
                value={toChoice(filters.lastActivityFailed)}
                onChange={setBooleanFilter('lastActivityFailed')}
                options={[
                  { value: 'yes', label: 'Failed - email / WhatsApp did not go through' },
                  { value: 'no', label: 'Not failed' },
                ]}
              />
            </FilterField>
            <FilterField label="Contact Details">
              <Flex gap={8}>
                {CONTACT_FILTERS.map(({ name, label }) => (
                  <Select
                    key={name}
                    allowClear
                    style={{ flex: 1, minWidth: 0 }}
                    placeholder={`${label}: any`}
                    value={toChoice(filters[name])}
                    onChange={setBooleanFilter(name)}
                    options={[
                      { value: 'yes', label: `Has ${label}` },
                      { value: 'no', label: `No ${label}` },
                    ]}
                  />
                ))}
              </Flex>
            </FilterField>
          </FilterBar>
        </div>
        <PageActions
          primary={{
            label: "Add Lead",
            shortLabel: "Add",
            icon: <UserAddOutlined />,
            className: "add-lead-btn",
            onClick: () => navigate("/leads/new"),
          }}
          maxInline={3}
          actions={[
            {
              key: "tags",
              // With leads ticked this adds tags to them; otherwise it
              // opens the tag list.
              label: selectedRecords.length ? `Tags (${selectedRecords.length})` : "Tags",
              icon: <TagsOutlined />,
              keepInline: true,
              onClick: () => (selectedRecords.length ? setOpenBulkTagsModal(true) : setOpenTagsModal(true)),
            },
            {
              key: "email",
              label: "Send Email",
              icon: <MailOutlined />,
              onClick: () => setOpenEmailDrawer(true),
            },
            {
              key: "whatsapp",
              label: "Send Whatsapp Template",
              icon: <MessageOutlined />,
              onClick: () => setOpenWAMessageDrawer(true),
            },
            {
              key: "import",
              label: "Import Leads",
              icon: <UploadOutlined />,
              onClick: () => setOpenImportDrawer(true),
            },
          ]}
        />
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            // Controlled, so the count above and the ticks always agree and
            // Clear unticks them.
            selectedRowKeys: selectedRecords.map((record) => record.id),
            // Keep ticks from other pages, so a bulk send can cover more
            // than one page of leads.
            preserveSelectedRowKeys: true,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['20', '50', '100', '200'],
            onChange: handleTableChange,
          }}
          scroll={{
            x: 'max-content',        // Enables horizontal scroll
            y: 'calc(100vh - 350px)' // Vertical scroll
          }}
          sticky
        />
      </div>

      {/* Both send drawers render their own Drawer. */}
      <SendWAMessageDrawer
        open={openWAMessageDrawer}
        onClose={() => setOpenWAMessageDrawer(false)}
        selectedRecords={selectedRecords}
        onSent={() => fetchLeads(currentPage, pageSize)}
      />
      <SendEmailDrawer
        open={openEmailDrawer}
        onClose={() => setOpenEmailDrawer(false)}
        selectedRecords={selectedRecords}
        onSent={() => fetchLeads(currentPage, pageSize)}
      />
      <ManageLeadTagsModal
        open={openTagsModal}
        onClose={() => setOpenTagsModal(false)}
        tags={leadTags || []}
        onTagsChange={(next) => setLeadTags((list) => (
          typeof next === 'function' ? next(list || []) : next
        ))}
      />
      <BulkEditLeadTagsModal
        open={openBulkTagsModal}
        onClose={() => setOpenBulkTagsModal(false)}
        leads={selectedRecords}
        tags={leadTags || []}
        onCreateTag={createTag}
        onApplied={applyBulkTags}
        onManageTags={() => {
          setOpenBulkTagsModal(false);
          setOpenTagsModal(true);
        }}
      />
      <ImportLeadsModal
        open={openImportDrawer}
        onClose={() => setOpenImportDrawer(false)}
        onSuccess={() => fetchLeads(currentPage, pageSize)}
      />
    </div >
  );
};
export default LeadsTable;
